import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { roundToDecimalPlaces } from '@semios/app-platform-common'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { keyBy } from 'lodash'

export const defaultValuesRowHeight = 70

export const evapotranspirationTimestampToValuesDictionaryMaker = (
  etcTimeseries: ({ timestamp: string; value: number | null } & object)[],
  kcTimeseries: ({ timestamp: string; value: number | null } & object)[],
) => {
  const etc = timestampToValuesDictionaryMaker(etcTimeseries) as unknown as Record<
    string,
    { timestamp: string; value: number | null } & object
  >

  const etcWithKc = Object.entries(etc).reduce<
    Record<string, { timestamp: string; value: number | null; kc: number }>
  >((acc, [timestamp, etcValue]) => {
    const kcValue = kcTimeseries.find((kc) => kc.timestamp === timestamp)

    if (typeof kcValue?.value === 'number') {
      acc[timestamp] = { ...etcValue, kc: roundToDecimalPlaces(kcValue.value, 2) || kcValue.value }
    }

    return acc
  }, {})

  return etcWithKc
}

export const timestampToValuesDictionaryMaker = (data: ({ timestamp: string } & object)[]) => {
  return keyBy(data ?? [], 'timestamp')
}

// in-block orchard weather stations
export const weatherTimestampToValuesDictionaryMaker = (
  data: routes.Values.Response,
  valueType: WeatherValueTypes,
  stationLngLat: keyof NonNullable<routes.Values.Response['points']>,
) => {
  return timestampToValuesDictionaryMaker(
    data?.points?.[stationLngLat]?.values?.[valueType]?.[0]?.timeseries ?? [],
  )
}

export const regionsTimestampToValuesDictionaryMaker = (
  data: routes.Values.Response,
  valueType: RegionValuesTypes,
  regionId: keyof NonNullable<routes.Values.Response['regions']>,
) => {
  return timestampToValuesDictionaryMaker(
    data?.regions?.[regionId]?.values?.[valueType]?.[0]?.timeseries ?? [],
  )
}

export type TIrrigationZoneValueTypes = keyof NonNullable<
  routes.Values.Response['irrigationZones']
>[string]['values']

export const blockTimestampToValuesDictionaryMaker = (
  data: routes.Values.Response,
  valueType: BlockValuesTypes,
  blockId: keyof NonNullable<routes.Values.Response['blocks']>,
) => {
  return timestampToValuesDictionaryMaker(data?.blocks?.[blockId]?.values?.[valueType]?.[0]?.timeseries ?? [])
}

type WeatherValueTypes =
  VV.DomainTypes.Weather.TTimeseriesValueTypeKeysMerged | VV.DomainTypes.Chill.TTimeseriesValueTypeKeysMerged

type BlockValuesTypes =
  | VV.DomainTypes.Alternaria.TTimeseriesValueTypeKeysMerged
  | VV.DomainTypes.DegreeDaysInsect.TTimeseriesValueTypeKeysMerged
  | VV.DomainTypes.DegreeDaysCrop.TTimeseriesValueTypeKeysMerged
  | VV.DomainTypes.CherryPowderyMildew.TTimeseriesValueTypeKeysMerged
  | VV.DomainTypes.GrapePowderyMildew.TTimeseriesValueTypeKeysMerged

type RegionValuesTypes =
  | keyof VV.DomainTypes.Weather.TValuesReturnWithMetaIgnoringKeying['regions']
  | keyof VV.DomainTypes.DegreeDaysInsect.TValuesReturnWithMetaIgnoringKeying['regions']
  | keyof VV.DomainTypes.DegreeDaysCrop.TValuesReturnWithMetaIgnoringKeying['regions']
